import { css, html, LitElement, TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";
import { ACHIEVEMENTS, Achievement, isFamilyCapstone } from "../shared/achievements.js";
import { avatarStyle, profileInitial } from "../shared/colors.js";
import {
  GameLocation,
  LOCATION_WIDE,
  START_LOCATION_ID,
  locationById,
  locationForFamily,
  locationUnlockMessage,
} from "../shared/location.js";
import { StoreController } from "./controller.store.js";
import { backIcon } from "./icons.js";
import { navigate } from "./nav.js";
import { appStore } from "./store.js";
import { globalStyles } from "./styles.global.js";
import "./component.level-badge.js";
import "./component.location-backdrop.js";
import "./component.medal.js";
import "./component.profile-modal.js";

@customElement("reading-bee-achievements")
export class ReadingBeeAchievements extends LitElement {
  static override styles = [
    globalStyles,
    css`
      :host {
        display: block;
        min-height: 100%;
      }

      .page {
        position: relative;
        min-height: 100dvh;
        min-width: 0;
        max-width: 100%;
        overflow-x: clip;
        display: grid;
        grid-template-rows: auto 1fr;
      }

      header {
        position: sticky;
        top: 0;
        z-index: 2;
        display: grid;
        grid-template-columns: 44px 1fr auto;
        align-items: center;
        gap: 0.6rem;
        padding: calc(0.7rem + var(--inset-top)) 1rem 0.8rem;
        background: linear-gradient(to bottom, rgba(12, 11, 9, 0.58), rgba(12, 11, 9, 0.22));
        border-bottom: 1px solid transparent;
      }

      .header-right {
        display: flex;
        align-items: center;
        gap: 0.55rem;
      }

      .badge-btn {
        width: 52px;
        height: 58px;
        padding: 0;
        border-radius: 12px;
        transition: transform var(--time-normal) ease;
      }

      .badge-btn:hover {
        transform: scale(1.06);
      }

      .avatar {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        display: grid;
        place-items: center;
        font-weight: 700;
        font-size: 1.05rem;
        line-height: 1;
        letter-spacing: 0;
        box-shadow: 0 0 0 3px transparent;
        transition:
          box-shadow var(--time-normal) ease,
          transform var(--time-normal) ease;
      }

      .avatar:hover {
        transform: scale(1.08);
        box-shadow: 0 0 0 3px var(--color-1);
      }

      .coins {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        min-height: 36px;
        padding: 0.15rem 0.65rem 0.15rem 0.3rem;
        border-radius: 999px;
        background: #1a1713;
        border: 1px solid rgba(232, 184, 74, 0.35);
        color: var(--color-1);
        font-weight: 800;
        transition:
          transform var(--time-normal) ease,
          border-color var(--time-normal) ease;
      }

      .coins:hover {
        transform: scale(1.06);
        border-color: var(--color-1);
      }

      .coin-dot {
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background: radial-gradient(circle at 35% 30%, #fff6c8, #e8b84a 58%, #9a6c1e);
        box-shadow: inset 0 0 0 1px rgba(255, 246, 200, 0.4);
      }

      header h1 {
        margin: 0;
        font-size: 1.2rem;
        text-align: center;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .back {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        display: grid;
        place-items: center;
        color: var(--color-primary-text);
        background: #1a1713;
        border: 1px solid var(--color-panel-border);
      }

      .body {
        position: relative;
        z-index: 1;
        width: min(720px, 100%);
        min-width: 0;
        margin: 0 auto;
        padding: 1.2rem 1.2rem calc(2rem + env(safe-area-inset-bottom));
      }

      .hero {
        position: relative;
        isolation: isolate;
        display: grid;
        justify-items: center;
        width: 100%;
        text-align: center;
        gap: 0.35rem;
        margin: 0.2rem 0 1.4rem;
        padding: 1.15rem 1rem 1.2rem;
        border-radius: 22px;
        border: 2px solid transparent;
        background: #1a1713;
        overflow: hidden;
        cursor: pointer;
      }

      .hero-scene {
        position: absolute;
        inset: 0;
        z-index: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        pointer-events: none;
      }

      .hero::after {
        content: "";
        position: absolute;
        inset: 0;
        z-index: 1;
        background: linear-gradient(to top, rgba(12, 11, 9, 0.72), rgba(12, 11, 9, 0.22));
        pointer-events: none;
      }

      .hero > *:not(.hero-scene) {
        position: relative;
        z-index: 2;
      }

      .hero.selected {
        border-color: var(--color-1);
        box-shadow:
          0 0 0 1px rgba(232, 184, 74, 0.35),
          0 10px 28px rgba(0, 0, 0, 0.28);
      }

      .hero reading-bee-medal {
        width: 88px;
        height: 99px;
      }

      .hero p {
        margin: 0;
        text-shadow: 0 1px 8px rgba(12, 11, 9, 0.9);
      }

      .level-label {
        color: var(--color-1);
        font-weight: 800;
        font-size: 1.15rem;
      }

      .grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 0.75rem;
      }

      .card {
        position: relative;
        display: grid;
        justify-items: center;
        gap: 0.3rem;
        padding: 0.7rem 0.45rem 0.8rem;
        border-radius: 18px;
        background: #1a1713;
        border: 1px solid var(--color-panel-border);
        text-align: center;
        min-width: 0;
        overflow: visible;
      }

      .card.locked {
        background: #14110e;
      }

      .card.has-location {
        isolation: isolate;
      }

      .card-scene {
        position: absolute;
        inset: 0;
        z-index: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: inherit;
        filter: grayscale(1);
        opacity: 0.55;
        pointer-events: none;
      }

      .card.unlocked-location {
        cursor: pointer;
      }

      .card.unlocked-location .card-scene {
        filter: none;
        opacity: 0.92;
      }

      .card.has-location::after {
        content: "";
        position: absolute;
        inset: 0;
        z-index: 1;
        border-radius: inherit;
        background: linear-gradient(to top, rgba(12, 11, 9, 0.78), rgba(12, 11, 9, 0.28));
        pointer-events: none;
      }

      .card.unlocked-location::after {
        background: linear-gradient(to top, rgba(12, 11, 9, 0.55), rgba(12, 11, 9, 0.12));
      }

      .card.selected {
        border-color: var(--color-1);
        box-shadow:
          0 0 0 1px rgba(232, 184, 74, 0.35),
          0 8px 22px rgba(0, 0, 0, 0.28);
      }

      .card.has-location > *:not(.card-scene):not(.tip) {
        position: relative;
        z-index: 2;
      }

      .card.has-location .name,
      .card.has-location .hint {
        text-shadow: 0 1px 8px rgba(12, 11, 9, 0.9);
      }

      .tip {
        position: absolute;
        bottom: calc(100% + 0.5rem);
        left: 50%;
        z-index: 8;
        width: max-content;
        max-width: min(11.5rem, 70vw);
        padding: 0.45rem 0.65rem;
        border-radius: 12px;
        background: #1a1713;
        color: var(--color-primary-text);
        font-size: 0.78rem;
        font-weight: 700;
        line-height: 1.25;
        text-wrap: balance;
        pointer-events: none;
        opacity: 0;
        visibility: hidden;
        transform: translateX(-50%) translateY(6px);
        box-shadow: 0 10px 24px rgba(0, 0, 0, 0.4);
        transition:
          opacity var(--time-normal) ease,
          transform var(--time-normal) ease,
          visibility var(--time-normal) ease;
      }

      .tip::after {
        content: "";
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        border: 6px solid transparent;
        border-top-color: #1a1713;
      }

      .card.tip-open .tip {
        opacity: 1;
        visibility: visible;
        transform: translateX(-50%) translateY(0);
      }

      @media (hover: hover) and (pointer: fine) {
        .card.has-location:hover .tip,
        .card.has-location:focus-visible .tip {
          opacity: 1;
          visibility: visible;
          transform: translateX(-50%) translateY(0);
        }
      }

      .name {
        font-weight: 800;
        font-size: 0.82rem;
        line-height: 1.2;
      }

      .hint {
        margin: 0;
        font-size: 0.74rem;
        line-height: 1.25;
        color: var(--color-primary-text-muted);
      }
    `,
  ];

  @state() private tipId: string | null = null;

  constructor() {
    super();
    new StoreController(this);
  }

  override connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener("pointerdown", this.onWindowPointerDown);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener("pointerdown", this.onWindowPointerDown);
  }

  override render(): TemplateResult {
    const profile = appStore.currentProfile;
    if (!profile) {
      return html`
        <div class="page"><p>No profile selected.</p></div>
      `;
    }
    const earnedIds = new Set(profile.achievements);
    const unlockedLocations = new Set(profile.unlockedLocations);
    const currentLocationId = profile.currentLocationId;
    const start = locationById(START_LOCATION_ID);
    return html`
      <div class="page">
        <reading-bee-location-backdrop></reading-bee-location-backdrop>
        <header>
          <button class="back" aria-label="Back" @click=${() => navigate("reading")}>${backIcon}</button>
          <h1>Achievements</h1>
          <div class="header-right">
            <button class="badge-btn" aria-label="Achievements" @click=${() => navigate("achievements")}>
              <reading-bee-level-badge .level=${profile.level}></reading-bee-level-badge>
            </button>
            <button class="coins" aria-label="Shop" @click=${() => navigate("shop")}>
              <span class="coin-dot"></span>
              ${profile.coins}
            </button>
            <reading-bee-profile-modal>
              <button
                slot="open-button"
                class="avatar"
                aria-label="Profile"
                style=${avatarStyle(profile.primaryColor, profile.secondaryColor)}>
                ${profileInitial(profile.name)}
              </button>
            </reading-bee-profile-modal>
          </div>
        </header>
        <div class="body">
          <button
            class="hero ${currentLocationId === START_LOCATION_ID ? "selected" : ""}"
            aria-label="Select the ${start.name}"
            aria-pressed=${currentLocationId === START_LOCATION_ID}
            @click=${() => this.selectLocation(START_LOCATION_ID)}>
            <img class="hero-scene" src=${LOCATION_WIDE} alt="" />
            <reading-bee-medal .value=${profile.level}></reading-bee-medal>
            <p class="level-label">Level ${profile.level}</p>
            <p>${profile.name} is reading at level ${profile.level}.</p>
          </button>
          <div class="grid">
            ${ACHIEVEMENTS.map((item) => this.card(item, earnedIds.has(item.id), unlockedLocations, currentLocationId))}
          </div>
        </div>
      </div>
    `;
  }

  private card(
    item: Achievement,
    earned: boolean,
    unlockedLocations: Set<string>,
    currentLocationId: string,
  ): TemplateResult {
    const location = isFamilyCapstone(item) ? locationForFamily(item.family) : undefined;
    const locationUnlocked = Boolean(location && unlockedLocations.has(location.id));
    const selected = Boolean(location && currentLocationId === location.id);
    const classes = [
      "card",
      earned ? "earned" : "locked",
      location ? "has-location" : "",
      locationUnlocked ? "unlocked-location" : "",
      selected ? "selected" : "",
      this.tipId === item.id ? "tip-open" : "",
    ]
      .filter(Boolean)
      .join(" ");
    return html`
      <div
        class=${classes}
        data-tip=${item.id}
        tabindex=${location ? 0 : -1}
        @click=${(event: MouseEvent) => this.onCardClick(event, item, location, locationUnlocked)}>
        ${
          location
            ? html`
                <img class="card-scene" src=${location.wide} alt="" />
              `
            : ""
        }
        <reading-bee-medal .value=${item.number} ?grayscale=${!earned}></reading-bee-medal>
        <span class="name">${item.title}</span>
        <p class="hint">${item.description}</p>
        ${
          location && !locationUnlocked
            ? html`
                <span class="tip" role="tooltip">${locationUnlockMessage(location)}</span>
              `
            : ""
        }
      </div>
    `;
  }

  private selectLocation = (locationId: string): void => {
    this.tipId = null;
    appStore.setCurrentLocation(locationId);
  };

  private onCardClick = (
    event: MouseEvent,
    item: Achievement,
    location: GameLocation | undefined,
    locationUnlocked: boolean,
  ): void => {
    if (!location) return;
    event.stopPropagation();
    if (!locationUnlocked) {
      this.tipId = this.tipId === item.id ? null : item.id;
      return;
    }
    this.selectLocation(location.id);
  };

  private onWindowPointerDown = (event: PointerEvent): void => {
    if (!this.tipId) return;
    const card = this.renderRoot.querySelector(`[data-tip="${this.tipId}"]`);
    if (card && event.composedPath().includes(card)) return;
    this.tipId = null;
  };
}

declare global {
  interface HTMLElementTagNameMap {
    "reading-bee-achievements": ReadingBeeAchievements;
  }
}
